// apps/vendor initial-session hydration gate (ENG-128).
//
// A cold sign-in lands on /auth/callback, exchanges the OIDC code for tokens
// (which Set-Cookie's a fresh refresh cookie on .molaris.ai), then does a FULL
// page reload to "/" (AuthCallback: window.location.replace). That reload wipes
// the auth-client's in-memory token/session, so the vendor shell's bootstrap
// must re-hydrate the session from scratch on the next paint.
//
// Re-hydration goes through auth-client getCurrentUser(): sessionStorage fast
// path first, else a GET /me cookie probe + POST /refresh. Right after sign-in
// that /me probe can 401 SPURIOUSLY — a brief read-after-write window while the
// just-minted refresh cookie/session propagates (documented in
// packages/auth-client/src/client.ts, "/me was already known to 401 spuriously
// during the PKCE handshake"). getCurrentUser() surfaces that transient 401 as
// a plain `null`, indistinguishable from a genuinely logged-out visitor.
//
// The old bootstrap called getCurrentUser() exactly once and treated that first
// `null` as "not signed in", dropping ~1/3 of cold sign-ins to the login screen
// even though a valid session was moments from hydrating. This helper gives the
// session one bounded chance to settle: on an initial `null`, wait briefly and
// re-load once before concluding "logged out". A genuinely anonymous visitor
// still lands on sign-in — just one short probe later.
//
// Pure + dependency-injected (`loadSession`, `sleep`) so the race is unit-
// testable without a browser or real timers, mirroring vendor-mfa-gate.ts.

export interface HydrateInitialSessionInput<S> {
  /**
   * Load the current session (auth-client getCurrentUser). Resolves the session
   * when one is hydrated, or `null` when none is available yet — including the
   * transient immediate-401 case this helper exists to absorb.
   */
  loadSession: () => Promise<S | null>;
  /**
   * How many extra attempts to make after the first `null` before giving up.
   * Default 1 — a single retry covers the post-bootstrap hydration window
   * without materially delaying a genuinely logged-out visitor.
   */
  retries?: number;
  /** Delay between attempts, ms. Default 150. */
  delayMs?: number;
  /** Injectable delay (tests). Defaults to a real setTimeout-backed sleep. */
  sleep?: (ms: number) => Promise<void>;
}

const DEFAULT_RETRIES = 1;
const DEFAULT_DELAY_MS = 150;

function realSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolve the vendor shell's initial session, tolerating the post-sign-in
 * hydration race. Returns the first non-null session; on a null first result it
 * retries up to `retries` times (with `delayMs` between attempts) before
 * resolving `null`. Never throws for an absent session — `loadSession` owns the
 * "no session" contract (auth-client getCurrentUser resolves null, never
 * rejects, on a failed probe).
 */
export async function hydrateInitialSession<S>(
  input: HydrateInitialSessionInput<S>,
): Promise<S | null> {
  const retries = input.retries ?? DEFAULT_RETRIES;
  const delayMs = input.delayMs ?? DEFAULT_DELAY_MS;
  const sleep = input.sleep ?? realSleep;

  let session = await input.loadSession();
  let attemptsLeft = retries;
  while (session === null && attemptsLeft > 0) {
    attemptsLeft -= 1;
    await sleep(delayMs);
    session = await input.loadSession();
  }
  return session;
}
